/**
 * IB-701 — presentation helpers for the Setup tab tiles (Categories + Policies).
 *
 * Tile icons are auto-derived on the frontend only: a short code from the name's initials and a
 * deterministic colour picked from the fixed Tailwind palette (tailwind.config.js). Neither is
 * persisted — the CRUD endpoints have no colour/code column, and IB-701 deliberately keeps it
 * that way (there is no colour picker in the forms).
 */

/**
 * Fixed icon-colour palette, drawn only from tailwind.config.js tokens (blue / green / red /
 * dark-blue / charcoal). `colourFor` indexes into this deterministically by id so a given tile
 * keeps the same colour across renders.
 */
export const ICON_PALETTE = [
    'tw-bg-blue-15 tw-text-blue-75',
    'tw-bg-green-25 tw-text-charcoal-75',
    'tw-bg-red-25 tw-text-red',
    'tw-bg-blue-25 tw-text-dark-blue',
    'tw-bg-charcoal-15 tw-text-charcoal-75',
];

/** Deterministic icon colour for a tile, keyed by its id (stable across renders). */
export const colourFor = (id) => {
    const index = Math.abs(Number(id) || 0) % ICON_PALETTE.length;

    return ICON_PALETTE[index];
};

/**
 * Derive a short uppercase code from a name's word initials, e.g.
 *   "Ground & Air Services" → "GAS", "Catering Supplies" → "CS", "Water" → "W".
 * Falls back to the first two letters of a single long word, then to "?" for an empty name.
 * Capped at three characters so it fits the square icon.
 */
export const deriveCode = (name) => {
    const trimmed = (name || '').trim();

    if (!trimmed) {
        return '?';
    }

    const words = trimmed.split(/\s+/).filter((word) => /[a-z0-9]/i.test(word));

    if (words.length === 0) {
        return '?';
    }

    if (words.length === 1) {
        return words[0].slice(0, 2).toUpperCase();
    }

    return words
        .map((word) => word[0])
        .join('')
        .slice(0, 3)
        .toUpperCase();
};
